import React, { useEffect, useState, useMemo, useCallback } from "react";
import DataTable from "react-data-table-component";
import Header from "./Header";
import Footer from "./Footer";
import API_BASE_URL from "../Config";
import "../styles/global.css";
import "./styles/Treatment.css";

/* ────────── SVG Icon System ────────── */
const Icon = ({ d, size = 18, stroke = 1.8 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "block", flexShrink: 0 }}
  >
    {d}
  </svg>
);

const SearchIcon = (p) => (
  <Icon {...p} d={<><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>} />
);
const PlusIcon = (p) => (
  <Icon {...p} d={<><path d="M12 5v14M5 12h14" /></>} />
);
const EditIcon = (p) => (
  <Icon {...p} d={<><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" /></>} />
);
const TrashIcon = (p) => (
  <Icon {...p} d={<><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6 18 20a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /></>} />
);
const CloseIcon = (p) => (
  <Icon {...p} d={<><path d="M18 6 6 18M6 6l12 12" /></>} />
);
const ActivityIcon = (p) => (
  <Icon {...p} d={<><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></>} />
);
const ChevronLeftIcon = (p) => (
  <Icon {...p} d={<><path d="m15 18-6-6 6-6" /></>} />
);
const ChevronRightIcon = (p) => (
  <Icon {...p} d={<><path d="m9 18 6-6-6-6" /></>} />
);
const CheckIcon = (p) => (
  <Icon {...p} d={<><path d="M20 6 9 17l-5-5" /></>} />
);
const XCircleIcon = (p) => (
  <Icon {...p} d={<><circle cx="12" cy="12" r="10" /><path d="m15 9-6 6M9 9l6 6" /></>} />
);
const ImageIcon = (p) => (
  <Icon {...p} d={<><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-5-5L5 21" /></>} />
);
const CheckCircleIcon = (p) => (
  <Icon {...p} d={<><circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" /></>} />
);
const InfoIcon = (p) => (
  <Icon {...p} d={<><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></>} />
);

/* ────────── Custom Confirm Dialog ────────── */
const ConfirmDialog = ({ open, title, message, confirmLabel = "Confirm", confirmVariant = "danger", onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="cdlg-overlay" role="dialog" aria-modal="true" aria-labelledby="cdlg-title">
      <div className="cdlg-box">
        <div className={`cdlg-icon-wrap cdlg-icon-${confirmVariant}`}>
          {confirmVariant === "danger" ? (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
            </svg>
          ) : (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          )}
        </div>
        <h3 id="cdlg-title" className="cdlg-title">{title}</h3>
        <p className="cdlg-message">{message}</p>
        <div className="cdlg-actions">
          <button type="button" className="cdlg-btn cdlg-btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className={`cdlg-btn cdlg-btn-confirm cdlg-btn-${confirmVariant}`} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ────────── Multi-Select Dropdown ────────── */
const MultiSelect = ({ options, value, onChange, placeholder = "Filter by status…" }) => {
  const [open, setOpen] = useState(false);

  const toggleOption = (opt) => {
    if (value.includes(opt)) {
      onChange(value.filter((v) => v !== opt));
    } else {
      onChange([...value, opt]);
    }
  };

  const clearAll = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  return (
    <div className="bc-multiselect" style={{ position: "relative" }}>
      <div
        className="bc-multiselect__trigger"
        onClick={() => setOpen(!open)}
        tabIndex={0}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false);
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="bc-multiselect__label">
          {value.length === 0
            ? placeholder
            : value.map((v) => (
                <span key={v} className="bc-multiselect__tag">{v}</span>
              ))}
        </span>
        <span className="bc-multiselect__caret">
          {value.length > 0 && (
            <button className="bc-multiselect__clear" onClick={clearAll} tabIndex={-1} type="button">
              <XCircleIcon size={14} />
            </button>
          )}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .2s ease" }}>
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
      </div>

      {open && (
        <div className="bc-multiselect__dropdown" role="listbox" aria-multiselectable="true">
          {options.map((opt) => (
            <div
              key={opt}
              className={`bc-multiselect__option ${value.includes(opt) ? "is-selected" : ""}`}
              role="option"
              aria-selected={value.includes(opt)}
              onClick={() => toggleOption(opt)}
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && toggleOption(opt)}
            >
              <span className="bc-multiselect__check">
                {value.includes(opt) && <CheckIcon size={12} stroke={2.5} />}
              </span>
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ────────── iPhone-style Toggle ────────── */
const ToggleSwitch = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={onChange}
    className={`dept-toggle ${checked ? "is-on" : "is-off"}`}
    title={checked ? "Click to deactivate" : "Click to activate"}
  >
    <span className="dept-toggle__thumb" />
  </button>
);

/* ────────── Custom Pagination ────────── */
const CustomPagination = ({ currentPage, totalPages, onPageChange, perPage, totalRows, onPerPageChange }) => {
  const pages = useMemo(() => {
    const arr = [];
    const delta = 2;
    const left = currentPage - delta;
    const right = currentPage + delta + 1;
    let l;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= left && i < right)) arr.push(i);
    }
    const withEllipsis = [];
    arr.forEach((i) => {
      if (l) {
        if (i - l === 2) withEllipsis.push(l + 1);
        else if (i - l > 2) withEllipsis.push("...");
      }
      withEllipsis.push(i);
      l = i;
    });
    return withEllipsis;
  }, [currentPage, totalPages]);

  return (
    <div className="bc-pagination">
      <div className="bc-pagination__info">
        <span>
          Showing <b>{totalRows === 0 ? 0 : Math.min((currentPage - 1) * perPage + 1, totalRows)}</b>–
          <b>{Math.min(currentPage * perPage, totalRows)}</b> of <b>{totalRows}</b>
        </span>
        <select
          className="bc-pagination__perpage"
          value={perPage}
          onChange={(e) => onPerPageChange(Number(e.target.value))}
        >
          {[10, 25, 50, 100].map((n) => (
            <option key={n} value={n}>{n} / page</option>
          ))}
        </select>
      </div>
      <div className="bc-pagination__pages">
        <button
          className="bc-pagination__btn"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          aria-label="Previous page"
        >
          <ChevronLeftIcon size={16} />
        </button>

        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`ellipsis-${i}`} className="bc-pagination__ellipsis">…</span>
          ) : (
            <button
              key={p}
              className={`bc-pagination__btn ${p === currentPage ? "is-active" : ""}`}
              onClick={() => onPageChange(p)}
              aria-current={p === currentPage ? "page" : undefined}
            >
              {p}
            </button>
          )
        )}

        <button
          className="bc-pagination__btn"
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={() => onPageChange(currentPage + 1)}
          aria-label="Next page"
        >
          <ChevronRightIcon size={16} />
        </button>
      </div>
    </div>
  );
};

/* ────────── Success Toast ────────── */
const SuccessToast = ({ message, onClose }) => (
  <div className="dept-toast dept-toast--success" role="alert">
    <span className="dept-toast__icon">
      <CheckCircleIcon size={20} />
    </span>
    <span className="dept-toast__msg">{message}</span>
    <button className="dept-toast__close" onClick={onClose} type="button" aria-label="Close">
      <CloseIcon size={15} />
    </button>
  </div>
);

const ErrorToast = ({ message, onClose }) => (
  <div className="dept-toast dept-toast--error" role="alert">
    <span className="dept-toast__icon">
      <XCircleIcon size={20} />
    </span>
    <span className="dept-toast__msg">{message}</span>
    <button className="dept-toast__close" onClick={onClose} type="button" aria-label="Close">
      <CloseIcon size={15} />
    </button>
  </div>
);

/* ────────── Rich Textarea Field ────────── */
const RichTextarea = ({ id, label, placeholder, value, onChange, rows = 4, hint }) => (
  <div className="bc-form-group">
    <label className="bc-form-label" htmlFor={id}>{label}</label>
    <div className="bc-rich-textarea-wrap">
      <textarea
        id={id}
        className="bc-form-control bc-textarea-rich"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        rows={rows}
      />
      <div className="bc-textarea-footer">
        <span className="bc-textarea-hint">
          <InfoIcon size={13} />
          {hint}
        </span>
        <span className="bc-char-count">{value.length} chars</span>
      </div>
    </div>
  </div>
);

/* ────────── Main Component ────────── */
const Treatment = () => {
  const [loading, setLoading] = useState(false);
  const [clinic, setCategories] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);

  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [causes, setCauses] = useState("");
  const [symptoms, setSymptoms] = useState("");
  const [commonTypes, setCommonTypes] = useState("");
  const [status, setStatus] = useState("Active");
  const [image, setImage] = useState([]);
  const [icon, setIcon] = useState([]);
  const [preview, setPreview] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);

  const [messagealert, setMessageAlert] = useState("");
  const [messageType, setMessageType] = useState("");

  const [isEditMode, setIsEditMode] = useState(false);
  const [editSlug, setEditSlug] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [togglingSlug, setTogglingSlug] = useState(null);
  const [togglingHomeSlug, setTogglingHomeSlug] = useState(null);

  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false, title: "", message: "", onConfirm: null,
    confirmLabel: "Confirm", confirmVariant: "danger",
  });

  const showConfirm = useCallback(({ title, message, onConfirm, confirmLabel = "Confirm", confirmVariant = "danger" }) => {
    setConfirmDialog({ open: true, title, message, onConfirm, confirmLabel, confirmVariant });
  }, []);

  const closeConfirm = useCallback(() => {
    setConfirmDialog((prev) => ({ ...prev, open: false, onConfirm: null }));
  }, []);

  const token = localStorage.getItem("authToken");

  /* ── Image preview ── */
  useEffect(() => {
    if (!image || image.length === 0) return;
    const objectUrl = URL.createObjectURL(image[0]);
    setPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [image]);

  useEffect(() => {
    if (!icon || icon.length === 0) return;
    const objectUrl = URL.createObjectURL(icon[0]);
    setIconPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [icon]);

  /* ── Fetch Treatments ── */
  const fetchCategories = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}api/treatment?page=1&per_page=1000`, {
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => { if (data.status) setCategories(data.data || []); })
      .catch((err) => console.error("Error fetching treatments:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchCategories(); }, []);

  /* ── Filtered list ── */
  const filteredCategories = useMemo(() => {
    let result = clinic;
    if (filterText) {
      const ft = filterText.toLowerCase();
      result = result.filter(
        (d) => d.name?.toLowerCase().includes(ft) || d.slug?.toLowerCase().includes(ft)
      );
    }
    if (statusFilter.length > 0) {
      result = result.filter((d) => statusFilter.includes(d.status));
    }
    return result;
  }, [filterText, statusFilter, clinic]);

  useEffect(() => { setCurrentPage(1); }, [filterText, statusFilter]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredCategories.length / perPage));
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [filteredCategories.length, perPage, currentPage]);

  const flashMessage = (msg, type = "success") => {
    setMessageAlert(msg); setMessageType(type);
    setTimeout(() => { setMessageAlert(""); setMessageType(""); }, 4500);
  };

  /* ── Toggle Status ── */
  const handleStatusChange = (slug, currentStatus) => {
    const newStatus = currentStatus === "Active" ? "InActive" : "Active";
    showConfirm({
      title: "Update Status",
      message: `Are you sure you want to change status to "${newStatus}"?`,
      confirmLabel: "Yes, Update",
      confirmVariant: "warning",
      onConfirm: () => {
        closeConfirm();
        setTogglingSlug(slug);
        fetch(`${API_BASE_URL}api/treatment/status/${slug}`, {
          method: "GET",
          headers: { Accept: "application/json", "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) { flashMessage(`Status updated to ${newStatus} successfully!`); fetchCategories(); }
            else flashMessage("Failed to update status.", "danger");
          })
          .catch(() => flashMessage("Error while updating status.", "danger"))
          .finally(() => setTogglingSlug(null));
      },
    });
  };

  /* ── Toggle Home ── */
  const handleHomeToggle = (slug, currentIsHome) => {
    const newVal = currentIsHome == 1 ? 0 : 1;
    showConfirm({
      title: "Update Home Visibility",
      message: `Are you sure you want to ${newVal === 1 ? "show" : "hide"} this treatment on the home page?`,
      confirmLabel: "Yes, Update",
      confirmVariant: "warning",
      onConfirm: () => {
        closeConfirm();
        setTogglingHomeSlug(slug);
        fetch(`${API_BASE_URL}api/treatment/ishome/${slug}`, {
          method: "GET",
          headers: { Accept: "application/json", "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) {
              flashMessage(`Treatment ${newVal === 1 ? "added to" : "removed from"} home page!`);
              fetchCategories();
            } else flashMessage("Failed to update home visibility.", "danger");
          })
          .catch(() => flashMessage("Error while updating home visibility.", "danger"))
          .finally(() => setTogglingHomeSlug(null));
      },
    });
  };

  /* ── Reset form fields ── */
  const resetForm = () => {
    setName(""); setDesc(""); setCauses(""); setSymptoms(""); setCommonTypes("");
    setStatus("Active"); setImage([]); setIcon([]); setPreview(null); setIconPreview(null);
    setIsEditMode(false); setEditSlug(null);
  };

  /* ── Save / Update ── */
  const handleSaveClinic = (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("name", name);
    formData.append("is_home", 1);
    formData.append("status", status);
    formData.append("desc", desc);
    formData.append("causes", causes);
    formData.append("symptoms", symptoms);
    formData.append("common_types", commonTypes);

    if (image && image.length > 0) {
      for (let i = 0; i < image.length; i++) formData.append("images[]", image[i]);
    }
    if (icon && icon.length > 0) {
      for (let i = 0; i < icon.length; i++) formData.append("icon[]", icon[i]);
    }

    const url = isEditMode
      ? `${API_BASE_URL}api/treatment/${editSlug}`
      : `${API_BASE_URL}api/treatment`;

    fetch(url, {
      method: "POST",
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === true) {
          setShowModal(false);
          flashMessage(isEditMode ? "Treatment updated successfully!" : "Treatment added successfully!");
          resetForm();
          fetchCategories();
        } else {
          flashMessage("Failed to save treatment.", "danger");
        }
      })
      .catch(() => flashMessage("Something went wrong!", "danger"));
  };

  /* ── Edit ── */
  const handleEdit = (row) => {
    setIsEditMode(true);
    setEditSlug(row.slug);
    setName(row.name);
    setDesc(row.desc || "");
    setCauses(row.causes || "");
    setSymptoms(row.symptoms || "");
    setCommonTypes(row.common_types || "");
    setStatus(row.status);

    setPreview(
      row.image
        ? `${API_BASE_URL}public/treatment/${row.image.split(",")[0]}`
        : `${API_BASE_URL}public/noimage.jpg`
    );
    setIconPreview(
      row.icon
        ? `${API_BASE_URL}public/treatment_icon/${row.icon.split(",")[0]}`
        : null
    );

    setImage([]); setIcon([]);
    setShowModal(true);
  };

  /* ── Delete single ── */
  const handleDelete = (slug) => {
    showConfirm({
      title: "Delete Treatment",
      message: "Are you sure you want to delete this treatment? This action cannot be undone.",
      confirmLabel: "Yes, Delete",
      confirmVariant: "danger",
      onConfirm: () => {
        closeConfirm();
        fetch(`${API_BASE_URL}api/treatment/destroy/${slug}`, {
          method: "GET",
          headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) { flashMessage("Treatment deleted successfully!"); fetchCategories(); }
            else flashMessage("Failed to delete treatment.", "danger");
          })
          .catch(() => flashMessage("Error deleting treatment.", "danger"));
      },
    });
  };

  /* ── Bulk delete ── */
  const handleBulkDelete = () => {
    if (selectedRows.length === 0) return;
    const count = selectedRows.length;
    showConfirm({
      title: "Delete Selected",
      message: `Are you sure you want to delete ${count} selected treatment${count > 1 ? "s" : ""}? This action cannot be undone.`,
      confirmLabel: `Delete ${count} Item${count > 1 ? "s" : ""}`,
      confirmVariant: "danger",
      onConfirm: () => {
        closeConfirm();
        Promise.all(
          selectedRows.map((row) =>
            fetch(`${API_BASE_URL}api/treatment/destroy/${row.slug}`, {
              method: "GET",
              headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
            }).then((res) => res.json())
          )
        )
          .then(() => {
            flashMessage(`${count} treatment${count > 1 ? "s" : ""} deleted successfully!`);
            setSelectedRows([]); setToggleCleared(!toggleCleared); fetchCategories();
          })
          .catch(() => flashMessage("Error deleting treatments!", "danger"));
      },
    });
  };

  /* ── Open Add modal ── */
  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  /* ── Pagination ── */
  const totalRows = filteredCategories.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));
  const pagedData = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredCategories.slice(start, start + perPage);
  }, [filteredCategories, currentPage, perPage]);

  /* ────────── Table Columns ────────── */
  const columns = useMemo(
    () => [
      {
        name: "Icon",
        cell: (row) => (
          <div className="dept-img-wrap">
            <img
              src={row.icon ? `${API_BASE_URL}public/treatment_icon/${row.icon.split(",")[0]}` : `${API_BASE_URL}public/noimage.jpg`}
              alt={row.name}
              className="dept-img"
            />
          </div>
        ),
        width: "80px",
        ignoreRowClick: true,
      },
      {
        name: "Image",
        cell: (row) => (
          <div className="dept-img-wrap">
            <img
              src={row.image ? `${API_BASE_URL}public/treatment/${row.image.split(",")[0]}` : `${API_BASE_URL}public/noimage.jpg`}
              alt={row.name}
              className="dept-img"
            />
          </div>
        ),
        width: "80px",
        ignoreRowClick: true,
      },
      {
        name: "Name",
        selector: (row) => row.name,
        cell: (row) => <span className="bc-cell-name">{row.name}</span>,
        sortable: true,
        grow: 2,
      },
      {
        name: "Slug",
        selector: (row) => row.slug,
        cell: (row) => <span className="bc-slug-pill">{row.slug}</span>,
        sortable: true,
        grow: 2,
      },
      {
        name: "Description",
        selector: (row) => row.desc,
        cell: (row) => (
          <span className="bc-cell-desc" title={row.desc}>
            {row.desc ? (row.desc.length > 60 ? row.desc.substring(0, 60) + "…" : row.desc) : "—"}
          </span>
        ),
        grow: 3,
      },
      {
        name: "Status",
        selector: (row) => row.status,
        cell: (row) => (
          <ToggleSwitch
            checked={row.status === "Active"}
            disabled={togglingSlug === row.slug}
            onChange={() => handleStatusChange(row.slug, row.status)}
          />
        ),
        sortable: true,
        width: "100px",
        ignoreRowClick: true,
      },
      {
        name: "Show on Home",
        selector: (row) => row.is_home,
        cell: (row) => (
          <ToggleSwitch
            checked={row.is_home == 1}
            disabled={togglingHomeSlug === row.slug}
            onChange={() => handleHomeToggle(row.slug, row.is_home)}
          />
        ),
        sortable: true,
        width: "130px",
        ignoreRowClick: true,
      },
      {
        name: "Created",
        selector: (row) => row.created_at,
        cell: (row) => (
          <span className="bc-cell-muted">
            {row.created_at
              ? new Date(row.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
              : "—"}
          </span>
        ),
        sortable: true,
        width: "140px",
      },
      {
        name: "Actions",
        cell: (row) => (
          <div className="bc-row-actions">
            <button className="bc-ico-btn edit" onClick={() => handleEdit(row)} title="Edit">
              <EditIcon size={15} />
            </button>
            <button className="bc-ico-btn del" onClick={() => handleDelete(row.slug)} title="Delete">
              <TrashIcon size={15} />
            </button>
          </div>
        ),
        width: "110px",
        right: true,
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [togglingSlug, togglingHomeSlug]
  );

  /* ────────── DataTable Custom Styles ────────── */
  const tableStyles = {
    table: { style: { background: "transparent" } },
    headRow: {
      style: {
        background: "#f0fbf6",
        borderTopLeftRadius: "12px",
        borderTopRightRadius: "12px",
        borderBottom: "1px solid #d6efe2",
        minHeight: "52px",
      },
    },
    headCells: {
      style: {
        fontSize: "11.5px", fontWeight: 700, letterSpacing: "0.07em",
        textTransform: "uppercase", color: "#5b6b66",
        paddingLeft: "16px", paddingRight: "16px",
      },
    },
    rows: {
      style: {
        minHeight: "72px", fontSize: "14px", color: "#1f2a37",
        borderBottom: "1px solid #eef4f1 !important",
        transition: "background 0.18s ease",
      },
      highlightOnHoverStyle: { background: "#f6fcf9", borderBottomColor: "#e3f0e9", outline: "none" },
      stripedStyle: { background: "#fafefb" },
    },
    cells: { style: { paddingLeft: "16px", paddingRight: "16px" } },
    pagination: { style: { display: "none" } },
  };

  /* ────────── Render ────────── */
  return (
    <>
      <style>{`
        /* ── Rich Textarea ── */
        .bc-rich-textarea-wrap {
          border: 1.5px solid #d6efe2;
          border-radius: 12px;
          overflow: hidden;
          transition: border-color .15s, box-shadow .15s;
          background: #fff;
        }
        .bc-rich-textarea-wrap:focus-within {
          border-color: #10b981;
          box-shadow: 0 0 0 4px rgba(16,185,129,.10);
        }
        .bc-textarea-rich {
          border: none !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          resize: vertical;
          min-height: 100px;
          font-size: 13.5px;
          line-height: 1.6;
          padding: 12px 14px !important;
          width: 100%;
          font-family: inherit;
          color: #1f2a37;
          background: #fff;
          outline: none;
        }
        .bc-textarea-rich::placeholder { color: #a8bdb6; }
        .bc-textarea-rich:focus { outline: none; box-shadow: none !important; }
        .bc-textarea-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 7px 12px;
          background: #f6fbf8;
          border-top: 1px solid #eef4f1;
        }
        .bc-textarea-hint {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 11.5px;
          color: #8fa89f;
        }
        .bc-char-count {
          font-size: 11px;
          color: #aab8b3;
          font-variant-numeric: tabular-nums;
        }

        /* ── Section Divider ── */
        .bc-section-divider {
          display: flex;
          align-items: center;
          gap: 10px;
          margin: 20px 0 4px;
        }
        .bc-section-divider__label {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: #10b981;
          white-space: nowrap;
        }
        .bc-section-divider__line {
          flex: 1;
          height: 1px;
          background: linear-gradient(90deg, #d6efe2, transparent);
        }

        /* ── Confirm Dialog ── */
        .cdlg-overlay {
          position: fixed; inset: 0; z-index: 9999;
          background: rgba(15,31,26,.45); backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center;
          padding: 16px; animation: cdlgFadeIn .18s ease;
        }
        @keyframes cdlgFadeIn { from { opacity:0; } to { opacity:1; } }
        .cdlg-box {
          background: #fff; border-radius: 20px;
          box-shadow: 0 24px 60px rgba(15,31,26,.2), 0 4px 16px rgba(15,31,26,.08);
          padding: 32px 28px 24px; width: 100%; max-width: 400px;
          text-align: center; animation: cdlgSlideUp .22s cubic-bezier(.34,1.56,.64,1);
        }
        @keyframes cdlgSlideUp {
          from { opacity:0; transform: translateY(18px) scale(.97); }
          to   { opacity:1; transform: translateY(0) scale(1); }
        }
        .cdlg-icon-wrap {
          width: 60px; height: 60px; border-radius: 18px;
          display: flex; align-items: center; justify-content: center; margin: 0 auto 18px;
        }
        .cdlg-icon-danger  { background: #fff1f0; color: #e0413c; }
        .cdlg-icon-warning { background: #fffbeb; color: #d97706; }
        .cdlg-title { font-size: 18px; font-weight: 800; color: #0f1f1a; margin: 0 0 8px; }
        .cdlg-message { font-size: 14px; color: #6b7a74; line-height: 1.6; margin: 0 0 24px; }
        .cdlg-actions { display: flex; gap: 10px; justify-content: center; }
        .cdlg-btn {
          flex: 1; padding: 11px 20px; border-radius: 10px; border: none;
          font-size: 14px; font-weight: 600; cursor: pointer; transition: all .15s;
        }
        .cdlg-btn-cancel { background: #f1f5f9; color: #475569; border: 1.5px solid #e2e8f0; }
        .cdlg-btn-cancel:hover { background: #e2e8f0; }
        .cdlg-btn-confirm { color: #fff; }
        .cdlg-btn-danger  { background: linear-gradient(135deg,#ef4444,#f43f5e); box-shadow: 0 4px 14px rgba(239,68,68,.28); }
        .cdlg-btn-danger:hover  { box-shadow: 0 6px 20px rgba(239,68,68,.4); transform: translateY(-1px); }
        .cdlg-btn-warning { background: linear-gradient(135deg,#f59e0b,#d97706); box-shadow: 0 4px 14px rgba(245,158,11,.28); }
        .cdlg-btn-warning:hover { box-shadow: 0 6px 20px rgba(245,158,11,.4); transform: translateY(-1px); }
      `}</style>

      <div id="layout-wrapper">
        <Header />

        {loading && (
          <div className="bc-overlay">
            <div className="bc-spinner" />
          </div>
        )}

        <div className="main-content">
          <div className="page-content">
            <div className="bc-shell">

              {/* ── Page Header ── */}
              <div className="bc-head">
                <div>
                  <h2>Treatments</h2>
                  <p>Manage and organise all your treatments in one place.</p>
                </div>
                <button type="button" className="bc-add-btn" onClick={openAddModal}>
                  <PlusIcon size={18} />
                  Add Treatment
                </button>
              </div>

              {/* ── Toast Alert ── */}
              {messagealert && messageType === "success" && (
                <SuccessToast message={messagealert} onClose={() => setMessageAlert("")} />
              )}
              {messagealert && messageType === "danger" && (
                <ErrorToast message={messagealert} onClose={() => setMessageAlert("")} />
              )}

              {/* ── Main Card ── */}
              <div className="bc-card">
                {/* Toolbar */}
                <div className="bc-card-top">
                  <div className="bc-card-top-left">
                    <div className="bc-search-box">
                      <SearchIcon size={17} />
                      <input
                        type="text"
                        placeholder="Search by name or slug…"
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                      />
                    </div>
                    <MultiSelect
                      options={["Active", "InActive"]}
                      value={statusFilter}
                      onChange={setStatusFilter}
                      placeholder="Filter by status…"
                    />
                  </div>
                  <div className="bc-total-badge">
                    <ActivityIcon size={16} />
                    Total:&nbsp;<b>{filteredCategories.length}</b>
                  </div>
                </div>

                {/* Bulk action bar */}
                {selectedRows.length > 0 && (
                  <div className="bc-bulk-bar">
                    <div className="bc-bulk-count">
                      <span className="bc-num-badge">{selectedRows.length}</span>
                      treatment{selectedRows.length > 1 ? "s" : ""} selected
                    </div>
                    <div className="bc-bulk-actions">
                      <button
                        className="bc-bulk-btn ghost"
                        type="button"
                        onClick={() => { setSelectedRows([]); setToggleCleared(!toggleCleared); }}
                      >
                        Clear
                      </button>
                      <button
                        className="bc-bulk-btn danger"
                        type="button"
                        onClick={handleBulkDelete}
                      >
                        <TrashIcon size={14} />
                        Delete Selected
                      </button>
                    </div>
                  </div>
                )}

                {/* Data Table */}
                <DataTable
                  columns={columns}
                  data={pagedData}
                  customStyles={tableStyles}
                  highlightOnHover
                  responsive
                  striped
                  selectableRows
                  onSelectedRowsChange={({ selectedRows }) => setSelectedRows(selectedRows)}
                  clearSelectedRows={toggleCleared}
                  noDataComponent={
                    <div className="bc-empty">
                      <ActivityIcon size={40} />
                      <p>No treatments found</p>
                      <span>Try adjusting your search or add a new treatment</span>
                    </div>
                  }
                />

                {/* Custom Pagination */}
                {filteredCategories.length > 0 && (
                  <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    perPage={perPage}
                    totalRows={totalRows}
                    onPerPageChange={(n) => { setPerPage(n); setCurrentPage(1); }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* ────────── Add / Edit Modal ────────── */}
          {showModal && (
            <div
              className="bc-modal-overlay"
              onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
              aria-modal="true"
              role="dialog"
              aria-labelledby="treatModalTitle"
            >
              <div className="bc-modal" style={{ maxWidth: 680 }}>
                {/* Modal Header */}
                <div className="bc-modal__header">
                  <div className="bc-modal__header-left">
                    <div className="bc-modal__icon">
                      <ActivityIcon size={20} />
                    </div>
                    <div>
                      <p className="bc-modal__title" id="treatModalTitle">
                        {isEditMode ? "Edit Treatment" : "Add New Treatment"}
                      </p>
                      <p className="bc-modal__subtitle">
                        {isEditMode
                          ? "Update the details for this treatment"
                          : "Fill in the details to create a new treatment"}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="bc-modal__close"
                    onClick={() => setShowModal(false)}
                    aria-label="Close modal"
                  >
                    <CloseIcon size={16} />
                  </button>
                </div>

                {/* Modal Body */}
                <form onSubmit={handleSaveClinic}>
                  <div className="bc-modal__body">

                    {/* ── BASIC INFO SECTION ── */}
                    <div className="bc-section-divider">
                      <span className="bc-section-divider__label">Basic Information</span>
                      <span className="bc-section-divider__line" />
                    </div>

                    {/* Treatment Name */}
                    <div className="bc-form-group">
                      <label className="bc-form-label" htmlFor="treatName">
                        Treatment Name <span>*</span>
                      </label>
                      <input
                        type="text"
                        id="treatName"
                        className="bc-form-control"
                        placeholder="e.g. Physiotherapy"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        autoFocus
                      />
                    </div>

                    {/* Description */}
                    <RichTextarea
                      id="treatDesc"
                      label={<>Description <span>*</span></>}
                      placeholder={"Brief description of this treatment…"}
                      value={desc}
                      onChange={(e) => setDesc(e.target.value)}
                      rows={3}
                      hint="Overview of the treatment"
                    />

                    {/* ── MEDICAL DETAILS SECTION ── */}
                    <div className="bc-section-divider" style={{ marginTop: 24 }}>
                      <span className="bc-section-divider__label">Medical Details</span>
                      <span className="bc-section-divider__line" />
                    </div>

                    {/* Causes */}
                    <RichTextarea
                      id="treatCauses"
                      label="Causes"
                      placeholder={"List the main causes of this condition…\ne.g. Poor posture, muscle weakness, injury, genetic factors..."}
                      value={causes}
                      onChange={(e) => setCauses(e.target.value)}
                      rows={4}
                      hint="What causes this condition?"
                    />

                    {/* Symptoms */}
                    <RichTextarea
                      id="treatSymptoms"
                      label="Symptoms"
                      placeholder={"List common symptoms patients experience…\ne.g. Pain, swelling, limited range of motion, fatigue..."}
                      value={symptoms}
                      onChange={(e) => setSymptoms(e.target.value)}
                      rows={4}
                      hint="What symptoms indicate this condition?"
                    />

                    {/* Common Types */}
                    <RichTextarea
                      id="treatCommonTypes"
                      label="Common Types"
                      placeholder={"List the common types or variants…\ne.g. Acute, Chronic, Degenerative, Inflammatory..."}
                      value={commonTypes}
                      onChange={(e) => setCommonTypes(e.target.value)}
                      rows={4}
                      hint="Different types or classifications of this condition"
                    />

                    {/* ── MEDIA SECTION ── */}
                    <div className="bc-section-divider" style={{ marginTop: 24 }}>
                      <span className="bc-section-divider__label">Media</span>
                      <span className="bc-section-divider__line" />
                    </div>

                    {/* Image Upload */}
                    <div className="bc-form-group">
                      <label className="bc-form-label">Treatment Images</label>
                      {!preview ? (
                        <div className="dept-upload-area">
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={(e) => setImage(e.target.files)}
                          />
                          <div className="dept-upload-icon">
                            <ImageIcon size={32} />
                          </div>
                          <div className="dept-upload-label">
                            <strong>Click to upload</strong> or drag & drop
                            <br />
                            <span style={{ fontSize: "11px", color: "#b0bcb6" }}>PNG, JPG, WEBP — multiple files allowed</span>
                          </div>
                        </div>
                      ) : (
                        <div className="dept-preview-wrap">
                          <img src={preview} alt="Preview" className="dept-preview-img" />
                          <div className="dept-preview-info">
                            <div className="dept-preview-name">
                              {image && image.length > 0 ? `${image.length} file${image.length > 1 ? "s" : ""} selected` : "Current image"}
                            </div>
                            <div className="dept-preview-hint">
                              {image && image.length > 0
                                ? `${(Array.from(image).reduce((a, f) => a + f.size, 0) / 1024).toFixed(1)} KB total`
                                : "Upload new images to replace"}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="dept-preview-remove"
                            onClick={() => { setImage([]); setPreview(null); }}
                            title="Remove image"
                          >
                            <CloseIcon size={14} />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Treatment Icon */}
                    <div className="bc-form-group">
                      <label className="bc-form-label">Treatment Icon</label>
                      {!iconPreview ? (
                        <div className="dept-upload-area">
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={(e) => setIcon(e.target.files)}
                          />
                          <div className="dept-upload-icon"><ImageIcon size={32} /></div>
                          <div className="dept-upload-label">
                            <strong>Click to upload</strong> or drag & drop
                            <br />
                            <span style={{ fontSize: "11px", color: "#b0bcb6" }}>PNG, JPG, WEBP — multiple files allowed</span>
                          </div>
                        </div>
                      ) : (
                        <div className="dept-preview-wrap">
                          <img src={iconPreview} alt="Icon Preview" className="dept-preview-img" />
                          <div className="dept-preview-info">
                            <div className="dept-preview-name">
                              {icon && icon.length > 0
                                ? `${icon.length} file${icon.length > 1 ? "s" : ""} selected`
                                : "Current icon"}
                            </div>
                            <div className="dept-preview-hint">
                              {icon && icon.length > 0
                                ? `${(Array.from(icon).reduce((a, f) => a + f.size, 0) / 1024).toFixed(1)} KB total`
                                : "Upload new icon to replace"}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="dept-preview-remove"
                            onClick={() => { setIcon([]); setIconPreview(null); }}
                            title="Remove icon"
                          >
                            <CloseIcon size={14} />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* ── SETTINGS SECTION ── */}
                    <div className="bc-section-divider" style={{ marginTop: 24 }}>
                      <span className="bc-section-divider__label">Settings</span>
                      <span className="bc-section-divider__line" />
                    </div>

                    {/* Status Toggle */}
                    <div className="bc-form-group">
                      <label className="bc-form-label">Status</label>
                      <div className="dept-status-row">
                        <div>
                          <div className="dept-status-row-label">Active Status</div>
                          <div className="dept-status-row-hint">
                            Toggle to set this treatment as active or inactive
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <span className={`dept-status-indicator ${status === "Active" ? "active" : "inactive"}`}>
                            {status === "Active" ? "Active" : "Inactive"}
                          </span>
                          <ToggleSwitch
                            checked={status === "Active"}
                            onChange={() => setStatus(status === "Active" ? "InActive" : "Active")}
                          />
                        </div>
                      </div>
                    </div>

                  </div>

                  {/* Modal Footer */}
                  <div className="bc-modal__footer">
                    <button
                      type="button"
                      className="bc-btn-cancel"
                      onClick={() => setShowModal(false)}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="bc-btn-submit">
                      {isEditMode ? (
                        <><EditIcon size={15} />Update Treatment</>
                      ) : (
                        <><PlusIcon size={15} />Add Treatment</>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* ────────── Custom Confirm Dialog ────────── */}
          <ConfirmDialog
            open={confirmDialog.open}
            title={confirmDialog.title}
            message={confirmDialog.message}
            confirmLabel={confirmDialog.confirmLabel}
            confirmVariant={confirmDialog.confirmVariant}
            onConfirm={confirmDialog.onConfirm}
            onCancel={closeConfirm}
          />

          <Footer />
        </div>
      </div>
    </>
  );
};

export default Treatment;